/**
 * File Name          : application-insights.service.ts
 * Author             : Auto-generated
 * Version            : 0.0.14
 * Date               : 2026-03-18
 * Description        : Application Insights telemetry service for tracking user analytics,
 *                      page views, errors, and custom events.
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */

import { Injectable } from '@angular/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { ApplicationInsights, IExceptionTelemetry, IEventTelemetry, IPageViewTelemetry } from '@microsoft/applicationinsights-web';
import { environment } from '../../environments/environment';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;

  constructor(private router: Router) {
    this.initializeAppInsights();
  }

  private initializeAppInsights(): void {
    // Only initialize if connection string is provided (production)
    if (!environment.appInsights?.connectionString) {
      console.log('Application Insights disabled (no connection string)');
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: environment.appInsights.connectionString,
        enableAutoRouteTracking: true, // Auto-track route changes
        enableCorsCorrelation: true,
        // Exclude the file service from correlation headers — those custom
        // headers (Request-Id, traceparent) turn simple GETs into non-simple
        // requests that require CORS preflight, which SWA-managed Functions
        // don't support. See fileservice/docs/DEPLOYMENT.md#cors-issues.
        correlationHeaderExcludedDomains: ['artifacts.rwthrive.com'],
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        maxBatchSizeInBytes: 10000,
        maxBatchInterval: 15000,
        disableFetchTracking: false
      }
    });

    this.appInsights.loadAppInsights();
    this.appInsights.trackPageView(); // Track initial page view

    // Track route changes
    this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(event.urlAfterRedirects);
      });

    console.log('Application Insights initialized');
  }

  /**
   * Track a page view
   */
  trackPageView(name?: string, uri?: string): void {
    if (!this.appInsights) return;
    
    const pageView: IPageViewTelemetry = {
      name: name,
      uri: uri
    };
    this.appInsights.trackPageView(pageView);
  }

  /**
   * Track a custom event (e.g., button click, feature usage)
   */
  trackEvent(name: string, properties?: { [key: string]: string }): void {
    if (!this.appInsights) return;
    
    const event: IEventTelemetry = {
      name: name,
      properties: properties
    };
    this.appInsights.trackEvent(event);
  }

  /**
   * Track an exception/error
   */
  trackException(error: Error, severityLevel?: number): void {
    if (!this.appInsights) return;
    
    const exception: IExceptionTelemetry = {
      exception: error,
      severityLevel: severityLevel
    };
    this.appInsights.trackException(exception);
  }

  /**
   * Track a metric (e.g., custom performance measurement)
   */
  trackMetric(name: string, average: number, properties?: { [key: string]: string }): void {
    if (!this.appInsights) return;
    
    this.appInsights.trackMetric({ name, average }, properties);
  }

  /**
   * Set authenticated user context
   */
  setAuthenticatedUser(userId: string, accountId?: string): void {
    if (!this.appInsights) return;
    
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  /**
   * Clear authenticated user context (on logout)
   */
  clearAuthenticatedUser(): void {
    if (!this.appInsights) return;
    
    this.appInsights.clearAuthenticatedUserContext();
  }

  /**
   * Flush all pending telemetry
   */
  flush(): void {
    if (!this.appInsights) return;
    
    this.appInsights.flush();
  }
}
